import { useCallback, useEffect, useState } from "react";
import { getWorkerProfileDetailLink } from "./workerDetailedProfileApi.js";
import WorkerDetailedProfileDialog from "./WorkerDetailedProfileDialog.jsx";

/**
 * P147: the Detailed Worker Profile entry point on the Worker Profiles screen.
 *
 * A Worker Profile row is a dbo.WmxWorkers row, but the detailed record lives
 * on the Quick Register application it was promoted from, keyed by
 * ApplicationId. This component asks the backend to resolve that link and then
 * opens the very same editor the Applications screen uses - so a passport
 * number typed on either screen is immediately true on both, rather than being
 * stored twice and drifting apart.
 *
 * The negative case is rendered honestly rather than hidden: a worker created
 * outside the Quick Register flow has no application to hang the detail on, and
 * this says so in the backend's own words instead of showing an empty form
 * whose save would fail.
 */
export default function WorkerProfileDetailedSection({ workerId, onSaved }) {
  const [state, setState] = useState({ status: "idle", link: null, error: null });
  const [dialogOpen, setDialogOpen] = useState(false);

  useEffect(() => {
    if (!workerId) {
      setState({ status: "idle", link: null, error: null });
      return undefined;
    }

    let cancelled = false;
    setState({ status: "loading", link: null, error: null });

    getWorkerProfileDetailLink(workerId)
      .then((link) => {
        if (!cancelled) {
          setState({ status: "ready", link, error: null });
        }
      })
      .catch((error) => {
        if (!cancelled) {
          setState({ status: "error", link: null, error });
        }
      });

    return () => {
      cancelled = true;
    };
  }, [workerId]);

  const handleSaved = useCallback(
    (saved) => {
      // Lets the list and drawer behind this re-read the same worker, so a name
      // corrected in the editor cannot stay stale in the row behind it.
      onSaved?.(saved);
    },
    [onSaved],
  );

  const link = state.link;
  const canOpen = state.status === "ready" && link?.hasDetailedProfile === true && Boolean(link?.applicationId);

  return (
    <section className="wx-wp-dsec">
      <h3>โปรไฟล์แรงงานแบบละเอียด</h3>
      <p className="wx-wp-dsec-note">
        เติมข้อมูลตัวตน เอกสาร ที่อยู่ สุขภาพ ทักษะ การศึกษา ประสบการณ์ และวีซ่า เพื่อให้ใช้จับคู่งานได้จริง
        โดยไม่เปลี่ยนสถานะ e-KYC และไม่เปลี่ยนสถานะพร้อมจับคู่
      </p>

      {state.status === "loading" && <p className="wx-wp-dsec-note">กำลังตรวจสอบแฟ้มข้อมูลละเอียด...</p>}

      {state.status === "error" && (
        <p className="wx-wp-dsec-note" role="alert">
          ไม่สามารถตรวจสอบแฟ้มข้อมูลละเอียดได้ กรุณาลองเปิดใหม่อีกครั้ง
        </p>
      )}

      {state.status === "ready" && !canOpen && (
        <p className="wx-wp-dsec-note" role="status">
          {link?.unavailableReason ?? "แรงงานคนนี้ยังไม่มีแฟ้มข้อมูลละเอียดให้แก้ไข"}
        </p>
      )}

      {canOpen && (
        <>
          <button type="button" className="wx-button wx-button--primary" onClick={() => setDialogOpen(true)}>
            แก้ไขข้อมูลแรงงาน
          </button>
          <WorkerDetailedProfileDialog
            open={dialogOpen}
            applicationId={link.applicationId}
            onClose={() => setDialogOpen(false)}
            onSaved={handleSaved}
          />
        </>
      )}
    </section>
  );
}
